/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import ReactDOM from 'react-dom';
import debounce from 'lodash.debounce';
import {translate} from '@ion/core/src/locale/locale';
import RootManager from '@ion/dls/src/client/RootManager';
import {basemapTitle, BasemapTypes, MapToolPositions} from '../types/types';

export interface Coordinates {
  x: number;
  y: number;
}

// Parameters for the addComponent() helper function.
export type AddComponentParams = {
  id: string,
  position: MapToolPositions,
  component: JSX.Element,
  view: __esri.MapView,
  usePortal?: boolean,
  index?: number
};

// Parameters for the resolveBasemapLayers() helper function.
export type ResolveBasemapLayersParams = {
  basemap: any;
};

export type HitTestParms = {
  view: __esri.MapView,
  layerId: string,
  handler: Function,
  includeAny?: boolean,
  includeResults?: boolean
}

// Defines class name for basemap's title element
const BASEMAP_ITEM_TITLE_CLASSNAME = 'esri-basemap-gallery__item-title';

// defines the dom containers and their associated root apis ...
const roots = new RootManager();

/**
 * Provides a utility which allows a new component to be added to a SimpleMap
 * instance.
 *
 * ```typescript
 *    import React from 'react';
 *    import {addComponent} from '@ion/features/src/maps/helpers';
 *
 *    const component = (
 *      <ZoomInOutTool onZoomOut={function() {
 *        const {zoom} = view;
 *        view.goTo({zoom: zoom - 1});
 *      }} onZoomIn={function() {
 *        const {zoom} = view;
 *        view.goTo({zoom: zoom + 1});
 *      }} />
 *    );
 *    addComponent({
 *      id        : 'map-zoom-in-out',
 *      position  : 'bottom-right'
 *      component,
 *      view
 *    });
 *
 * ```
 */
export const addComponent = ({
  id,
  position,
  component,
  view,
  usePortal = false,
  index = 0
}: AddComponentParams) => {
  const node = id ? RootManager.createContainer(id) : document.createElement('div');
  node.id = id;
  if (usePortal) {
    ReactDOM.createPortal(component, node);
  } else {
    roots.render(component, node);
  }
  view.ui.add(node, {
    position,
    index
  });
  return component;
};

/**
 * Provides a utility which allows for removing an existing custom component by
 * its identifier.
 *
 * ```typescript
 *  import React from 'react';
 *  import {removeComponent} from '@ion/dls/src/maps/helpers';
 *
 *  removeComponent('map-layer-chooser', view);
 *
 * ```
 */
export const removeComponent = (id: string, view: __esri.MapView) => {
  const container = RootManager.getContainer(id);
  if (container) {
    roots.unmount(container);
  }
  view?.ui?.remove(id);
};

/**
 * Provides a utility to resolve a map's internal basemap layers. ESRI always
 * places map basemap reference layers (ex. label layers) "on top of" all other
 * layers (ex. graphics or feature layers), even if those other layers are
 * created after the basemap layers. This function moves the map's basemap
 * reference layers to the map's basemap base layers list, allowing graphics
 * and feature layers to be displayed over label layers, for example.
 *
 * ```typescript
 *    import {resolveBasemapLayers} from '@ion/features/src/maps/helpers';
 *
 *    resolveBasemapLayers(map);
 *
 * ```
 */
export const resolveBasemapLayers = ({
  basemap
}: ResolveBasemapLayersParams) => {
  const {baseLayers: {items: blItems}, referenceLayers: {items: rlItems}} = basemap;
  rlItems?.forEach(item => {
    blItems?.push(item);
  });
  if (rlItems) {
    basemap.referenceLayers.items = [];
  }
};

/**
 * Provides the hitTest implementation for all map gestures, invoking the given
 * handler when the event occurs on an item within the layer of interest.
 *
 * ```typescript
 *   const onSelect = (view, layerId, handler) => {
 *     view.on('click', function({x, y}) {
 *       hitTest({x, y}, {view, layerId, handler});
 *     });
 *   };
 *
 *   view.on('click', function({x, y}) {
 *     hitTest({x, y}, {
 *        view,
 *        layerId : 'some-layer',
 *        handler : (attributes, graphic, coordinates}) => {
 *         ...
 *        }
 *      });
 *   });
 *
 * ```
 */
export const hitTest = ({x, y}: Coordinates, {
  view,
  layerId,
  handler,
  includeAny = false,
  includeResults = false
}: HitTestParms) => {
  const coordinates = {x, y};
  view.hitTest(coordinates).then(({results}) => {
    const selected = results?.filter(({graphic}) => graphic?.layer?.id === layerId);
    if (selected?.length) {
      const graphic = selected[0].graphic;
      const {attributes} = graphic;
      handler(attributes, graphic, coordinates);
    } else if (includeAny) {
      handler(includeResults ? results : null);
    }
  });
};

/**
 * Provides a utility which allows for invoking a callback when an item within
 * the given layer is clicked, which, when true, invokes the provided handler
 * with a reference to the item (attributes), the graphic, and the coordinates
 * of the selected item.
 *
 * ```typescript
 *
 *   useEffect(() => {
 *      loadModules(['esri/layers/GraphicsLayer', 'esri/Graphic'])
 *       .then(([GraphicsLayer, Graphic]) => {
 *         const layer = new GraphicsLayer({
 *         graphics : markers.generate(assets, Graphic),
 *         id       : 'assets',
 *       });
 *       map.add(layer);
 *       onSelect(view, 'assets', onSelectedAsset);
 *     }).catch(err => {
 *       console.error(err);
 *     });
 *     return function() {
 *       view.graphics.removeAll();
 *     };
 *   }, [view]);
 *
 * ```
 *
 */
export const onSelect = (view: __esri.MapView, layerId: string, handler: Function) => {
  view.on('click', function({x, y}) {
    hitTest({x, y}, {view, layerId, handler});
  });
};

/**
 * Defines a utility to return basemap name
 *  * Capitalize each word (not first letter)
 *  * Remove any dash (-)
 *
 *  ```typescript
 *
 * import {getBasemapName} from '@ion/dls/src/maps/helpers'
 *
 * getBasemapName(basemap);  'gray-vector' -> 'grayVector'
 *
 * ```
 */
export const getBasemapName = (basemap: string): string => {
  return basemap?.replace(/([-\s]\w)/g, match => match.toUpperCase())?.replace(/-/g, '');
};

// Translating basemap title on the client side
export const translateBasemap = debounce(() => {
  Object.keys(basemapTitle).forEach((basemap: string) => {
    const el = getBasemapElement(basemapTitle[basemap]);
    if (el) {
      el.textContent = translate(`maps:basemaps.${getBasemapName(basemap)}`)
        ?.replace(/OpenStreetMap/i, match => `${match?.slice(0, 10)}​${match?.slice(10)}`);
    }
    translateBasemapTooltip(basemap);
  });
}, 150);

// Translating basemap tooltip on the client side
const translateBasemapTooltip = (basemap: string) => {
  const el = getBasemapElement(basemapTitle[basemap], false);
  if (el) {
    el.title = translate(`maps:basemaps.${getBasemapName(basemap)}`);
  }
};

// Getting basemap element by title and class
const getBasemapElement = (title: string, isTitle = true) => {
  const isOSM      = title === basemapTitle[BasemapTypes.osm];
  const titleQuery = `[title${!isOSM ? '' : '^'}="${title}"]`;
  const query      = `${titleQuery} ${isTitle ? `> .${BASEMAP_ITEM_TITLE_CLASSNAME}`: ''}`;
  const element    = document.querySelector(query);
  if (!element && isTitle) {
    return document.querySelector(`${titleQuery} span`);
  }
  return element;
};
